import axios, { AxiosError, type AxiosInstance } from 'axios';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/store/auth.store';
import { useEstablishmentStore } from '@/store/establishment.store';

const baseURL = import.meta.env.VITE_API_URL?.trim() || '';

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
});

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers['Authorization'] = `Bearer ${t}`;
  }
  const estId = useEstablishmentStore.getState().currentId;
  if (estId) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers['X-Establishment-Id'] = estId;
  }
  return cfg;
});

// Состояние refresh-цикла: пока бек выдаёт новый access — все 401 в одной очереди
// ждут результата, потом ретраятся. Без этого 5 параллельных запросов = 5 одновременных
// /api/auth/refresh, бэк гасит refresh-token в Redis и пользователь логаутится.
let refreshInflight: Promise<string | null> | null = null;
const pendingQueue: Array<(token: string | null) => void> = [];

async function refreshAccessToken(): Promise<string | null> {
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;
  try {
    const { data } = await axios.post<{ access_token: string }>(
      `${baseURL}/api/auth/refresh`,
      { refresh_token: refresh },
      { timeout: 10_000 },
    );
    const newToken = data.access_token;
    // Обновляем только accessToken, refreshToken остаётся прежним (long-lived).
    const cur = useAuthStore.getState();
    if (cur.user) {
      cur.setSession({
        accessToken: newToken,
        refreshToken: refresh,
        user: cur.user,
      });
    }
    return newToken;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<{ error?: string; code?: string }>) => {
    const status = err.response?.status;
    const original = err.config as (typeof err.config & { _retry?: boolean }) | undefined;
    const msg = err.response?.data?.error ?? err.message ?? 'Ошибка запроса';

    if (status === 401 && original && !original._retry) {
      // /refresh сам не должен пытаться рефрешиться рекурсивно.
      const url = original.url ?? '';
      if (url.includes('/api/auth/refresh') || url.includes('/api/auth/admin/login')) {
        useAuthStore.getState().logout();
        toast.error('Сессия истекла, войдите ещё раз');
        return Promise.reject(err);
      }

      original._retry = true;

      // Если refresh уже запущен — встаём в очередь.
      if (refreshInflight) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((token) => {
            if (!token) return reject(err);
            original.headers = original.headers ?? {};
            (original.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
            resolve(api(original));
          });
        });
      }

      refreshInflight = refreshAccessToken();
      const newToken = await refreshInflight;
      refreshInflight = null;

      // Раздаём результат всем ждущим в очереди.
      const queue = pendingQueue.splice(0);
      for (const cb of queue) cb(newToken);

      if (!newToken) {
        useAuthStore.getState().logout();
        toast.error('Сессия истекла, войдите ещё раз');
        return Promise.reject(err);
      }
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
      return api(original);
    }

    if (status === 401) {
      useAuthStore.getState().logout();
      toast.error('Сессия истекла, войдите ещё раз');
    } else if (status && status >= 400 && status < 500) {
      toast.error(msg);
    } else if (status && status >= 500) {
      toast.error(`Ошибка сервера: ${msg}`);
    }
    return Promise.reject(err);
  },
);

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await api.get<T>(url, { params });
  return data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.post<T>(url, body);
  return data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.patch<T>(url, body);
  return data;
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.put<T>(url, body);
  return data;
}

export async function del<T>(url: string): Promise<T> {
  const { data } = await api.delete<T>(url);
  return data;
}
